import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, Layers, Video, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

const CATEGORIES = ["Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "General"];

export default function AdminCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", mrp: "", price: "", description: "", image_url: "", category: "General", content_type: "course" });

  useEffect(() => { fetchCourses(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCourses = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, mrp: parseFloat(form.mrp), price: parseFloat(form.price) };
    if (editing) {
      await api.put(`/courses/${editing}`, payload);
    } else {
      await api.post("/courses", payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ title: "", mrp: "", price: "", description: "", image_url: "", category: "General", content_type: "course" });
    fetchCourses();
  };

  const handleEdit = (course) => {
    setForm({ title: course.title, mrp: course.mrp, price: course.price, description: course.description || "", image_url: course.image_url || "", category: course.category || "General", content_type: course.content_type || "course" });
    setEditing(course.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this course?")) {
      await api.delete(`/courses/${id}`);
      fetchCourses();
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-courses-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Courses
          </h1>
          <button
            data-testid="add-course-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ title: "", mrp: "", price: "", description: "", image_url: "", category: "General", content_type: "course" }); }}
            className="bg-[#0284C7] text-white font-bold py-2 px-4 text-sm flex items-center gap-2 transition-none"
          >
            <Plus size={16} /> Add
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white p-4 mb-4 space-y-3" data-testid="course-form">
            <input data-testid="course-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="course-mrp-input" placeholder="MRP" type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={inputClass} required />
              <input data-testid="course-price-input" placeholder="Price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} required />
            </div>
            <textarea data-testid="course-desc-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-20`} />
            <FileUpload
              label="Course Thumbnail"
              accept="image/*"
              uploadType="courses"
              currentUrl={form.image_url}
              onUpload={(url) => setForm({ ...form, image_url: url })}
            />
            <div className="mt-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Or paste Image URL</label>
              <input data-testid="course-image-input" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="course-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="course-type-select" value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} className={inputClass}>
                <option value="course">Course</option>
                <option value="book">Book / PDF</option>
              </select>
            </div>
            <button data-testid="course-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-3 px-6 text-sm transition-none">
              {editing ? "Update" : "Create"} Course
            </button>
          </form>
        )}

        {/* Course List */}
        <div className="space-y-2" data-testid="courses-list">
          {courses.map((course) => (
            <div key={course.id} className="bg-white p-3 flex gap-3 items-center">
              <div className="w-20 aspect-video bg-slate-200 flex-shrink-0 overflow-hidden">
                <img src={course.image_url || "https://via.placeholder.com/160x90"} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{course.title}</p>
                <p className="text-xs text-slate-500">{course.category} &middot; ₹{course.price}</p>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`manage-quizzes-${course.id}`} onClick={() => navigate(`/admin/courses/${course.id}/quizzes`)} className="p-2 text-purple-500" title="Quizzes">
                  <ClipboardList size={16} />
                </button>
                <button data-testid={`manage-live-${course.id}`} onClick={() => navigate(`/admin/courses/${course.id}/live-sessions`)} className="p-2 text-red-500" title="Live Classes & Recordings">
                  <Video size={16} />
                </button>
                <button data-testid={`manage-chapters-${course.id}`} onClick={() => navigate(`/admin/courses/${course.id}/chapters`)} className="p-2 text-[#0284C7]" title="Chapters">
                  <Layers size={16} />
                </button>
                <button data-testid={`edit-course-${course.id}`} onClick={() => handleEdit(course)} className="p-2 text-slate-500">
                  <Edit size={16} />
                </button>
                <button data-testid={`delete-course-${course.id}`} onClick={() => handleDelete(course.id)} className="p-2 text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {courses.length === 0 && <p className="text-sm text-slate-400 py-4">No courses yet.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
